/** Makes names unique within one archive: "a.txt", "a (2).txt", "a (3).txt". Case-insensitive, like most unzip tools. */
export function uniqueNames(names: string[]): string[] {
  const used = new Set<string>();
  return names.map(n => {
    const dot = n.lastIndexOf(".");
    const base = dot > 0 ? n.slice(0, dot) : n;
    const ext = dot > 0 ? n.slice(dot) : "";
    let out = n;
    let i = 1;
    while (used.has(out.toLowerCase())) { i++; out = `${base} (${i})${ext}`; }
    used.add(out.toLowerCase());
    return out;
  });
}

export const isZipName = (name: string) => /\.zip$/i.test(name);

/** A safe single file name for saving an extracted entry (drops folders and ".." segments). */
export function safeEntryName(path: string): string {
  const leaf = path.split(/[\\/]/).filter(p => p && p !== "." && p !== "..").pop();
  return leaf || "file";
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const u = ["KB", "MB", "GB"];
  let v = n / 1024, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${u[i]}`;
}

/**
 * A safe relative path for an extracted entry: folders are kept, but empty, "." and ".." segments, a leading
 * drive letter and any leading slash are dropped, so "../../evil.txt" becomes "evil.txt" and "/abs.txt" becomes "abs.txt".
 */
export function safeEntryPath(path: string): string {
  const parts = path.split(/[\\/]/).filter(p => p && p !== "." && p !== "..");
  if (parts.length > 0 && /^[a-zA-Z]:$/.test(parts[0])) parts.shift();
  return parts.length ? parts.join("/") : "file";
}

/** Like uniqueNames, but for paths: only the file name part is renamed, so "a/x.txt" and "a/x.txt" become "a/x.txt" and "a/x (2).txt". */
export function uniquePaths(paths: string[]): string[] {
  const used = new Set<string>();
  return paths.map(full => {
    const slash = full.lastIndexOf("/");
    const dir = slash >= 0 ? full.slice(0, slash + 1) : "";
    const leaf = full.slice(slash + 1);
    const dot = leaf.lastIndexOf(".");
    const base = dot > 0 ? leaf.slice(0, dot) : leaf;
    const ext = dot > 0 ? leaf.slice(dot) : "";
    let out = full;
    let i = 1;
    while (used.has(out.toLowerCase())) { i++; out = `${dir}${base} (${i})${ext}`; }
    used.add(out.toLowerCase());
    return out;
  });
}

export type ZipLevel = "store" | "fast" | "best";

export function zipCompressionOptions(level: ZipLevel): { compression: "STORE" | "DEFLATE"; compressionOptions?: { level: number } } {
  if (level === "store") return { compression: "STORE" };
  return { compression: "DEFLATE", compressionOptions: { level: level === "fast" ? 1 : 9 } };
}

/** The archive name for a new ZIP: what the person typed, else the first file's name, cleaned and ending in ".zip". */
export function archiveFileName(typed: string, firstFileName: string | undefined): string {
  const clean = (n: string) => n.replace(/\.zip$/i, "").replace(/[\\/:*?"<>|\u0000-\u001f]+/g, "").trim();
  const base = clean(typed) || clean((firstFileName ?? "").replace(/\.[^.]+$/, "")) || "archive";
  return `${base}.zip`;
}

/** Splits "dir/sub/file.txt" into the folder part ("dir/sub/") and the file name. */
export function splitPath(path: string): { dir: string; name: string } {
  const i = path.lastIndexOf("/");
  return { dir: path.slice(0, i + 1), name: path.slice(i + 1) };
}
